import {
  autoUpdate,
  FloatingPortal,
  FloatingTree,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions
} from '@floating-ui/react'
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  type PropsWithChildren,
  type ReactElement,
  type Ref,
  useEffect,
  useRef,
  useState
} from 'react'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import { container, submenu, trigger as triggerCss } from './ActionsMenu.css'

type TriggerProps = React.HTMLAttributes<HTMLElement> & {
  ref: Ref<HTMLElement>
}

const OPEN_DELAY = 250

type ActionsMenuProps = PropsWithChildren<{
  /** Receives `open` so the trigger can stay highlighted while the menu is hovered */
  trigger?: (props: TriggerProps, open: boolean) => ReactElement
  triggerOn?: 'hover' | 'click'
  /** Which edge of the trigger the menu hangs from — 'end' avoids overflowing past a right boundary (e.g. a modal) */
  align?: 'start' | 'end'
}>

const ActionsMenuInner = ({
  children,
  trigger,
  triggerOn = 'hover',
  align = 'start'
}: ActionsMenuProps): ReactElement => {
  const [open, setOpen] = useState<boolean>(false)
  const defaultTriggerRef = useRef<HTMLSpanElement | null>(null)
  const nodeId = useFloatingNodeId()

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    placement: `bottom-${align}`,
    open,
    onOpenChange: setOpen,
    strategy: 'fixed',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const hover = useHover(context, {
    enabled: triggerOn === 'hover',
    move: false,
    delay: { open: OPEN_DELAY, close: 150 }
  })
  const click = useClick(context, { enabled: triggerOn === 'click' })
  const dismiss = useDismiss(context, { bubbles: true })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss
  ])

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  // Fallback ref attachment for the default ellipsis trigger
  useEffect(() => {
    if (!trigger && defaultTriggerRef.current) {
      refs.setReference(defaultTriggerRef.current)
    }
  }, [trigger, refs])

  const triggerElement = trigger ? (
    trigger(
      {
        ref: refs.setReference as Ref<HTMLElement>,
        ...getReferenceProps()
      },
      open
    )
  ) : (
    <div className={container}>
      <span
        ref={defaultTriggerRef}
        className={triggerCss}
        {...getReferenceProps()}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </span>
    </div>
  )

  return (
    <>
      {triggerElement}
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 999 }}
            {...getFloatingProps()}
          >
            <div
              className={clsx(themeClass, submenu)}
              style={transitionStyles}
              onClick={() => setOpen(false)}
            >
              {children}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}

export const ActionsMenu = (props: ActionsMenuProps): ReactElement => (
  <FloatingTree>
    <ActionsMenuInner {...props} />
  </FloatingTree>
)
